/**
 * PosteingangAutoAktion - Klickbare Handlungsempfehlungen für den automatisierten Posteingang
 * Zeigt KI-Analyse-Ergebnisse und ermöglicht Ein-Klick-Aktionen.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Check, X, Clock, AlertTriangle, FileText, User, Building2,
  Loader2, ChevronDown, ChevronUp, Sparkles, Calendar, ListTodo,
  FolderPlus, Euro
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PosteingangAutoAktion({
  dokument,
  analyse,
  onAktionAusgefuehrt,
  onKlientZuordnen
}) {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [ausgefuehrteAktion, setAusgefuehrteAktion] = useState(null);

  const empfehlungen = analyse?.handlungsempfehlungen || [];
  const fristen = analyse?.fristen || [];
  const erkannteLeistungen = analyse?.erkannte_leistungen || [];

  // Wichtigkeit als Badge
  const wichtigkeitBadge = {
    hoch: { color: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle },
    mittel: { color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
    niedrig: { color: 'bg-slate-100 text-slate-600 border-slate-300', icon: FileText },
  };

  const wichtig = analyse?.wichtig || 'mittel';
  const WichtigIcon = wichtigkeitBadge[wichtig]?.icon || FileText;

  // Aktion ausführen
  const executeAktion = async (aktion, params = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/assistent/dokument/aktion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dokument_id: dokument.id,
          aktion: aktion,
          params: params
        })
      });

      const result = await response.json();

      if (result.success) {
        setAusgefuehrteAktion(aktion);
        toast.success(result.message || 'Aktion ausgeführt');
        onAktionAusgefuehrt?.(dokument.id, aktion, result);
      } else {
        toast.error(result.error || 'Aktion fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler beim Ausführen der Aktion');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Leistung eintragen
  const trageLeistungEin = async (leistung) => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/assistent/leistungen/eintragen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          klient_id: dokument.klient_id,
          leistung: {
            typ: leistung.kategorie,
            bezeichnung: leistung.name,
            betrag: leistung.betrag,
            leistungsgeber_name: leistung.traeger,
            start_datum: leistung.seit,
            status: 'aktiv',
            quelle: 'ki_extraktion',
            dokument_id: dokument.id,
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Leistung "${leistung.name}" eingetragen`);
      } else {
        toast.error(result.error || 'Fehler beim Eintragen');
      }
    } catch (error) {
      toast.error('Fehler beim Eintragen der Leistung');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Aktions-Icons
  const aktionIcons = {
    'aufgabe_erstellen': ListTodo,
    'aufgabe_aktualisieren': Check,
    'ablegen': FolderPlus,
    'frist_beachten': Calendar,
    'frist_verpasst': AlertTriangle,
    'widerspruch_aufgabe': AlertTriangle,
  };

  if (ausgefuehrteAktion === 'ablegen') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
        <Check className="w-5 h-5 text-green-600" />
        <span className="text-green-700 font-medium">Dokument abgelegt</span>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid={`posteingang-auto-${dokument.id}`}>
      {/* Header mit Zusammenfassung */}
      <div 
        className={`p-3 cursor-pointer transition-colors ${
          wichtig === 'hoch' ? 'bg-red-50 hover:bg-red-100' :
          wichtig === 'mittel' ? 'bg-amber-50 hover:bg-amber-100' :
          'bg-slate-50 hover:bg-slate-100'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`${wichtigkeitBadge[wichtig]?.color} border`}>
                <WichtigIcon className="w-3 h-3 mr-1" />
                {wichtig === 'hoch' ? 'Dringend' : wichtig === 'mittel' ? 'Prüfen' : 'Info'}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {analyse?.dokumenttyp || 'Unbekannt'}
              </Badge>
            </div>
            
            <p className="font-medium text-slate-800 truncate">
              {dokument.titel || dokument.dateiname}
            </p>
            
            {analyse?.absender && (
              <p className="text-sm text-slate-600 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {analyse.absender}
              </p>
            )}
            
            {analyse?.zusammenfassung && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                {analyse.zusammenfassung}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {fristen.length > 0 && (
              <Badge className={fristen[0].abgelaufen ? 'bg-red-600 text-white' : fristen[0].dringend ? 'bg-orange-500 text-white' : 'bg-blue-100 text-blue-700'}>
                <Calendar className="w-3 h-3 mr-1" />
                {fristen[0].datum_text}
              </Badge>
            )}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {/* Expandierter Bereich mit Aktionen */}
      {expanded && (
        <div className="border-t p-3 space-y-3">
          {/* Erkannte Leistungen */}
          {erkannteLeistungen.length > 0 && dokument.klient_id && (
            <div className="bg-violet-50 rounded-lg p-3">
              <p className="text-sm font-medium text-violet-700 flex items-center gap-1 mb-2">
                <Euro className="w-4 h-4" />
                Erkannte Leistungen
              </p>
              <div className="space-y-2">
                {erkannteLeistungen.map((l, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{l.icon}</span>
                      <div>
                        <p className="text-sm font-medium">{l.name}</p>
                        {l.betrag && <p className="text-xs text-slate-500">{l.betrag.toFixed(2)} €/Monat</p>}
                        {l.traeger && <p className="text-xs text-slate-400">{l.traeger}</p>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-violet-600 border-violet-300 hover:bg-violet-100"
                      onClick={() => trageLeistungEin(l)}
                      disabled={loading}
                      data-testid={`leistung-eintragen-${i}`}
                    >
                      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                      Eintragen
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Handlungsempfehlungen */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              Handlungsempfehlungen
            </p>
            
            {empfehlungen.map((e, i) => {
              const Icon = aktionIcons[e.aktion] || FileText;
              const isPrimary = e.prioritaet === 'hoch' || i === 0;
              
              return (
                <Button
                  key={i}
                  variant={isPrimary ? 'default' : 'outline'}
                  className={`w-full justify-start text-left h-auto py-2 ${
                    isPrimary ? 'bg-indigo-600 hover:bg-indigo-700' : ''
                  }`}
                  onClick={() => executeAktion(e.aktion, {
                    aufgabe_id: e.aufgabe_id,
                    frist: e.frist,
                    titel: e.text,
                    klient_id: dokument.klient_id,
                  })}
                  disabled={loading || ausgefuehrteAktion === e.aktion}
                  data-testid={`aktion-${e.aktion}`}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : ausgefuehrteAktion === e.aktion ? (
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                  ) : (
                    <Icon className={`w-4 h-4 mr-2 ${isPrimary ? '' : 'text-slate-500'}`} />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{e.text}</p>
                    {e.beschreibung && (
                      <p className={`text-xs ${isPrimary ? 'text-indigo-200' : 'text-slate-500'}`}>
                        {e.beschreibung}
                      </p>
                    )}
                  </div>
                </Button>
              );
            })}
          </div>

          {/* Schnellaktionen wenn kein Klient zugeordnet */}
          {!dokument.klient_id && analyse?.erkannter_klient && (
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-sm text-green-700 flex items-center gap-1 mb-2">
                <User className="w-4 h-4" />
                Erkannter Klient: <strong>{analyse.erkannter_klient.vorname} {analyse.erkannter_klient.nachname}</strong>
              </p>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onKlientZuordnen?.(dokument.id, analyse.erkannter_klient.id)}
              >
                <Check className="w-3 h-3 mr-1" />
                Klient zuordnen
              </Button>
            </div>
          )}

          {/* Erkannte Personen & Institutionen */}
          <ErkannteEntitaeten dokument={dokument} onAktionAusgefuehrt={onAktionAusgefuehrt} />
        </div>
      )}
    </Card>
  );
}


// ===== Erkannte Personen & Institutionen =====
function ErkannteEntitaeten({ dokument, onAktionAusgefuehrt }) {
  const [loadingAction, setLoadingAction] = useState(null);
  const entitaeten = dokument?.erkannte_entitaeten;
  if (!entitaeten) return null;

  const personen = entitaeten.personen || [];
  const institutionen = entitaeten.institutionen || [];
  if (personen.length === 0 && institutionen.length === 0) return null;

  const handleAnlegen = async (typ, data) => {
    setLoadingAction(`${typ}-${data.nachname || data.name}`);
    try {
      if (typ === 'person') {
        const res = await fetch(`${BACKEND_URL}/api/netzwerk/natuerliche-personen`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vorname: data.vorname || '',
            nachname: data.nachname,
            rolle: data.rolle || 'kontaktperson',
            organisation: data.organisation || '',
            telefon: data.telefon || '',
            email: data.email || '',
          }),
        });
        if (res.ok) {
          const newPerson = await res.json();
          if (dokument.klient_id && newPerson.id) {
            await fetch(`${BACKEND_URL}/api/netzwerk/verknuepfungen`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                quell_id: newPerson.id,
                quell_typ: 'natuerliche_person',
                ziel_id: dokument.klient_id,
                ziel_typ: 'klient',
                beziehung_art: data.rolle || 'kontaktperson',
              }),
            });
          }
          toast.success(`Person "${data.vorname} ${data.nachname}" angelegt und verknüpft`);
          onAktionAusgefuehrt?.();
        }
      } else {
        const res = await fetch(`${BACKEND_URL}/api/netzwerk/juristische-personen`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: data.name,
            typ: data.typ || 'sonstiges',
            adresse: data.adresse || '',
            telefon: data.telefon || '',
          }),
        });
        if (res.ok) {
          const newInst = await res.json();
          if (dokument.klient_id && newInst.id) {
            await fetch(`${BACKEND_URL}/api/netzwerk/verknuepfungen`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                quell_id: newInst.id,
                quell_typ: 'juristische_person',
                ziel_id: dokument.klient_id,
                ziel_typ: 'klient',
                beziehung_art: data.typ || 'sonstiges',
              }),
            });
          }
          toast.success(`Institution "${data.name}" angelegt und verknüpft`);
          onAktionAusgefuehrt?.();
        }
      }
    } catch (e) {
      toast.error(`Fehler: ${e.message}`);
    }
    setLoadingAction(null);
  };

  return (
    <div className="bg-blue-50 rounded-lg p-3 space-y-2" data-testid="erkannte-entitaeten">
      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-1">
        <User className="w-3.5 h-3.5" />
        Erkannte Personen & Institutionen
      </p>

      {personen.map((p, i) => (
        <div key={`p-${i}`} className="flex items-center gap-2 bg-white rounded-lg p-2 text-sm">
          <User className="w-4 h-4 text-blue-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">{p.vorname} {p.nachname}</span>
            <span className="text-xs text-slate-400 ml-1">({p.rolle})</span>
            {p.organisation && <span className="text-xs text-slate-500 ml-1">{p.organisation}</span>}
          </div>
          {p.status === 'gefunden' && p.verknuepft ? (
            <Badge variant="secondary" className="text-[10px] shrink-0">
              <Check className="w-3 h-3 mr-0.5" /> {p.auto_verknuepft ? 'Auto-verknüpft' : 'Verknüpft'}
            </Badge>
          ) : p.status === 'vorschlag' ? (
            <Button
              size="sm" variant="outline" className="text-xs h-7 shrink-0"
              onClick={() => handleAnlegen('person', p)}
              disabled={!!loadingAction}
              data-testid={`anlegen-person-${i}`}
            >
              {loadingAction === `person-${p.nachname}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderPlus className="w-3 h-3 mr-1" />}
              Anlegen
            </Button>
          ) : null}
        </div>
      ))}

      {institutionen.map((inst, i) => (
        <div key={`i-${i}`} className="flex items-center gap-2 bg-white rounded-lg p-2 text-sm">
          <Building2 className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">{inst.name}</span>
            <span className="text-xs text-slate-400 ml-1">({inst.typ})</span>
          </div>
          {inst.status === 'gefunden' && inst.verknuepft ? (
            <Badge variant="secondary" className="text-[10px] shrink-0">
              <Check className="w-3 h-3 mr-0.5" /> {inst.auto_verknuepft ? 'Auto-verknüpft' : 'Verknüpft'}
            </Badge>
          ) : inst.status === 'vorschlag' ? (
            <Button
              size="sm" variant="outline" className="text-xs h-7 shrink-0"
              onClick={() => handleAnlegen('institution', inst)}
              disabled={!!loadingAction}
              data-testid={`anlegen-institution-${i}`}
            >
              {loadingAction === `institution-${inst.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderPlus className="w-3 h-3 mr-1" />}
              Anlegen
            </Button>
          ) : null}
        </div>
      ))}
    </div>
  );
}
